import React, { useEffect, useMemo, useState } from "react";
import { Button, Table } from "antd";
import type { TableColumnsType, TableProps } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ModalUser from "./ModalUser";
import { useConfirmModal } from "./ModalDelete";
import Swal from "sweetalert2";
import ModalAppCoin from "./ModalAppCoin";
import Input from "antd/es/input/Input";

type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];

export interface DataType {
  _id: string;
  username: string;
  phone: string;
  role: string;
  coins: number;
}

const ListUser: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [dataUser, setDataUser] = useState([]);
  const [isShowCreate, setIsShowCreate] = useState(false);
  const [isShowEdit, setIsShowEdit] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const Cookie = require("js-cookie");
  const token = Cookie.get("access_token");
  const [dataEdit, setDataEdit] = useState<DataType>();
  const [isShowAppCoin, setIsShowAppCoin] = useState(false);
  const [idUser, setIdUser] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const { showConfirm, contextHolder } = useConfirmModal();

  const handleDelete = (user: DataType) => {
    showConfirm({
      title: "Xoá người dùng",
      content: `Bạn có chắc muốn xoá người dùng ${user.username} không?`,
      onOk: async () => {
        await axios
          .delete(`${process.env.REACT_APP_URL_API}/users/${user._id}`, {
            headers: {
              Authorization: `Bearer ${token} `,
              accept: "*/*",
            },
          })
          .then((data) => {
            if (data.status === 200) {
              Swal.fire({
                icon: "success",
                title: "Xoá tài khoản thành công",
                text: "Vui lòng đợi ít phút. ",
                timer: 1000,
                timerProgressBar: true,
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  icon: "custom-icon",
                },
              });
              setRefreshTrigger((prev) => prev + 1);
            }
          })
          .catch((err) => {
            return err;
          });
      },
    });
  };

  const columns: TableColumnsType<DataType> = [
    {
      title: "Name",
      align: "center",
      render: (data: DataType) => {
        return data.username;
      },
    },
    {
      title: "Role",
      align: "center",
      render: (data: DataType) => {
        return data.role;
      },
    },
    {
      title: "Phone",
      align: "center",
      render: (data: DataType) => {
        return "****";
      },
    },
    {
      title: "COINS",
      align: "center",
      render: (data: DataType) => {
        return data.coins;
      },
    },
    {
      title: "Action",
      align: "center",
      render: (data: DataType) => {
        return (
          <div className="flex gap-3 justify-center items-center">
            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => {
                setIsShowAppCoin(true);
                setIdUser(data._id);
              }}
            />
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setIsShowEdit(true);
                setDataEdit(data);
              }}
            />
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(data)}
            />
            {contextHolder}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    const userInfoRaw = localStorage.getItem("user_info");
    const userInfo = userInfoRaw ? JSON.parse(userInfoRaw) : null;
    const uriUserList = userInfo.role === "ADMIN" ? `${process.env.REACT_APP_URL_API}/users` : `${process.env.REACT_APP_URL_API}/users/all`;
    const fetchData = async () => {
      try {
        const response = await axios.get(
          uriUserList,
          {
            headers: {
              Authorization: `Bearer ${token} `,
              accept: "*/*",
            },
          }
        );
        const sortedData = response.data.sort((a: DataType, b: DataType) =>
          b._id.localeCompare(a._id)
        );

        setDataUser(sortedData);
      } catch (err) {
        return err;
      }
    };

    fetchData();
  }, [token, refreshTrigger]);

  const filteredUsers = useMemo(() => {
    return dataUser.filter((user: DataType) =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, dataUser]);

  return (
    <div className="admin-card">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="admin-card__title" style={{ margin: 0 }}>
          <span className="admin-card__title-dot" />
          DANH SÁCH NGƯỜI DÙNG HỆ THỐNG
        </div>
        <Button
          type="primary"
          className="btn-admin-primary"
          icon={<PlusCircleOutlined />}
          onClick={() => setIsShowCreate(true)}
        >
          Tạo mới User
        </Button>
      </div>

      <div className="mb-5">
        <Input
          type="search"
          placeholder="🔍 Tìm kiếm theo tên người dùng..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="admin-search-input"
          style={{ width: "100%", maxWidth: 400 }}
        />
      </div>

      <div className="admin-table-wrap">
        <Table<DataType>
          columns={[
            {
              title: "Tên tài khoản",
              align: "center",
              render: (data: DataType) => (
                <span style={{ fontWeight: 800, color: "#ffffff" }}>{data.username}</span>
              ),
            },
            {
              title: "Vai trò (Role)",
              align: "center",
              render: (data: DataType) => {
                const isAdmin = data.role?.toUpperCase() === "ADMIN";
                return (
                  <span
                    className={`admin-role-badge ${
                      isAdmin ? "admin-role-badge--admin" : "admin-role-badge--user"
                    }`}
                  >
                    {data.role || "USER"}
                  </span>
                );
              },
            },
            {
              title: "Số điện thoại",
              align: "center",
              render: () => <span style={{ color: "#7a99c6" }}>****</span>,
            },
            {
              title: "Số xu (Coins)",
              align: "center",
              render: (data: DataType) => (
                <span style={{ fontWeight: 800, color: "#00FF88", fontSize: 15 }}>
                  {data.coins?.toLocaleString() ?? 0} XU
                </span>
              ),
            },
            {
              title: "Thao tác",
              align: "center",
              render: (data: DataType) => {
                return (
                  <div className="flex gap-2 justify-center items-center">
                    <Button
                      title="Nạp xu"
                      className="btn-action-add"
                      icon={<PlusCircleOutlined />}
                      onClick={() => {
                        setIsShowAppCoin(true);
                        setIdUser(data._id);
                      }}
                    />
                    <Button
                      title="Chỉnh sửa"
                      className="btn-action-edit"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setIsShowEdit(true);
                        setDataEdit(data);
                      }}
                    />
                    <Button
                      title="Xóa"
                      className="btn-action-delete"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(data)}
                    />
                    {contextHolder}
                  </div>
                );
              },
            },
          ]}
          dataSource={filteredUsers}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
        />
      </div>

      <ModalUser
        isShowCreate={isShowCreate}
        isShowEdit={isShowEdit}
        onCanEdit={() => setIsShowEdit(false)}
        onCancel={() => setIsShowCreate(false)}
        onRefesh={() => setRefreshTrigger((prev) => prev + 1)}
        data={dataEdit}
      />
      <ModalAppCoin
        id={idUser}
        isShowCoin={isShowAppCoin}
        onCanCoin={() => setIsShowAppCoin(false)}
        onRefesh={() => setRefreshTrigger((prev) => prev + 1)}
      />
    </div>
  );
};

export default ListUser;
